'use client';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ArrowLeft } from 'lucide-react';
import { Github } from '@/components/ui/Icons';
import Image from 'next/image';
import Link from 'next/link';
import { projectsApi } from '@/lib/api';
import { Project } from '@/types';

export default function AllProjects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectsApi.getAll().then(data => {
      setProjects(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const placeholders: Project[] = [
    { id: 1, title: 'E-Commerce Platform', slug: 'ecommerce', description: 'A full-featured e-commerce platform built with Django REST Framework and React.', long_description: '', tech_stack: ['Django', 'DRF', 'React', 'PostgreSQL'], image: null, live_url: '#', github_url: '#', featured: true, order: 1, created_at: '' },
    { id: 2, title: 'Task Management App', slug: 'tasks', description: 'A Kanban-style task management application with real-time updates.', long_description: '', tech_stack: ['Django', 'Next.js', 'WebSockets', 'Docker'], image: null, live_url: '#', github_url: '#', featured: true, order: 2, created_at: '' },
    { id: 3, title: 'Portfolio API', slug: 'portfolio', description: 'RESTful API powering this portfolio website.', long_description: '', tech_stack: ['Django', 'DRF', 'PostgreSQL', 'Docker'], image: null, live_url: '#', github_url: '#', featured: true, order: 3, created_at: '' },
  ];

  const displayProjects = projects.length > 0 ? projects : placeholders;

  return (
    <div style={{ paddingTop: '8rem', minHeight: '100vh', paddingBottom: '4rem' }}>
      <div className="container">
        <Link href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', marginBottom: '2rem', fontFamily: 'Outfit, sans-serif' }}>
          <ArrowLeft size={16} /> Back to Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="accent-line" />
          <h1 className="section-title">All Projects</h1>
          <p className="section-subtitle">A comprehensive list of things I&apos;ve built</p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '2rem' }}>
          {displayProjects.map((project, i) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="card"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ height: '180px', borderRadius: '8px', overflow: 'hidden', marginBottom: '1rem', background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
                {project.image ? (
                  <Image src={project.image} alt={project.title} width={400} height={180} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
                ) : (
                  <div style={{ textAlign: 'center', color: 'var(--text-subtle)' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>💻</div>
                  </div>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1.2rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>{project.title}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1rem' }}>{project.description}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.5rem' }}>
                  {project.tech_stack.map(tech => <span key={tech} className="tag">{tech}</span>)}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: 'auto' }}>
                {project.live_url && (
                  <a href={project.live_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', fontSize: '0.85rem' }}>
                    <ExternalLink size={14} /> Live Demo
                  </a>
                )}
                {project.github_url && (
                  <a href={project.github_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ flex: 1, justifyContent: 'center', fontSize: '0.85rem' }}>
                    <Github size={14} /> GitHub
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
